import { AxiosResponse } from "axios"
import { useApiCommand } from "hooks/core/useApiCommand"
import {
  convertToDecipherXML,
  convertToConfirmitXML,
  convertToWord,
  downloadHTML,
} from "api/Engine/convert"

import {
  ConversionOptions,
  ConversionType,
  EXPORT_VARIABLE_TYPES,
  VARIABLE_STYLE,
  SQL_EXPORT_TYPES,
  DATA_FORMAT,
  CSV_CAT_OPTION,
} from "types/engineExport.types"

import {
  downloadDatamap,
  getDatamapCSV as getDatamapCSVApi,
  InitialiseDataReporting,
  RunQuery,
} from "api/Engine/data-reporting"
import { exportToJSON, exportToJSONL, exportToSPS, exportToSAV, exportToSQL, exportToDQD, exportToCSV } from "api/Engine/export"
import { QueryInstance } from "src/types/engine.types"
import type { InitialiseMode } from "types/runOnLoad.types"

// Define the return type interface for the useEngine hook
interface UseEngineReturn {
  // Conversion operations
  convertToXMLDecipher: (
    id: string,
    options: ConversionOptions
  ) => Promise<AxiosResponse<Blob>>
  isConvertingToXMLDecipher: boolean

  convertToXMLConfirmit: (
    id: string,
    options: ConversionOptions
  ) => Promise<AxiosResponse<Blob>>
  isConvertingToXMLConfirmit: boolean

  convertToWordPreview: (
    id: string,
    options: ConversionOptions
  ) => Promise<AxiosResponse<unknown>>
  isConvertingToWordPreview: boolean

  exportToHTML: (
    id: string,
    options: ConversionOptions,
    filename: string,
    type: ConversionType,
    variableNames: EXPORT_VARIABLE_TYPES
  ) => Promise<AxiosResponse<string>>
  isExportingHTML: boolean

  // Data operations
  downloadDatamapCSV: (
    id: string,
    filename: string,
    template: boolean,
    groupSections: boolean,
    variableNames: EXPORT_VARIABLE_TYPES,
    variableStyle?: VARIABLE_STYLE,
    includeQuestionText?: boolean
  ) => Promise<AxiosResponse<Blob>>
  isDownloadingDatamapCSV: boolean

  getDatamapCSV: (
    id: string,
    groupSections: boolean,
    variableNames: EXPORT_VARIABLE_TYPES,
    variableStyle?: VARIABLE_STYLE,
    includeQuestionText?: boolean,
    variant?: string,
    template?: boolean
  ) => Promise<AxiosResponse<string>>
  isFetchingDatamapCSV: boolean

  initialiseData: (id: string, onload?: InitialiseMode) => Promise<AxiosResponse<unknown>>
  isInitialisingData: boolean

  RunEngineQuery: (
    instanceID: QueryInstance,
    query: string,
    isAsync: boolean
  ) => Promise<AxiosResponse<unknown>>
  isRunningEngineQuery: boolean

  exportJSON: (
    instanceID: QueryInstance,
    filename: string,
    dataFormat?: DATA_FORMAT
  ) => Promise<File | null>
  isExportingJSON: boolean

  exportJSONL: (
    instanceID: QueryInstance,
    filename: string,
    useLabels?: boolean,
    flatSeparator?: string
  ) => Promise<File | null>
  isExportingJSONL: boolean

  exportSQL: (
    instanceID: QueryInstance,
    sqlType: SQL_EXPORT_TYPES,
    project: string,
    filename: string
  ) => Promise<AxiosResponse<Blob>>
  isExportingSQL: boolean

  exportSPS: (
    instanceID: QueryInstance,
    varType: EXPORT_VARIABLE_TYPES,
    filename: string
  ) => Promise<File | null>
  isExportingSPS: boolean

  exportSAV: (
    instanceID: QueryInstance,
    varType: EXPORT_VARIABLE_TYPES,
    filename: string
  ) => Promise<File | null>
  isExportingSAV: boolean

  exportDQD: (
    instanceID: QueryInstance,
    filename: string,
    dataFormat?: DATA_FORMAT
  ) => Promise<File | null>
  isExportingDQD: boolean

  exportCSV: (
    instanceID: QueryInstance,
    varType?: EXPORT_VARIABLE_TYPES,
    catOption?: CSV_CAT_OPTION
  ) => Promise<AxiosResponse<string>>
  isExportingCSV: boolean
}

/**
 * Custom hook `useEngine` for handling various engine-related operations.
 *
 * This hook encapsulates the logic for converting documents, downloading files, checking data, initializing reporting, and running queries.
 * Commands run through `useApiCommand` (authed axios + react-query mutation).
 *
 * @returns An object containing functions for various engine operations and their loading states.
 *
 * @example
 * const {
 *   convertToXMLDecipher,
 *   isConvertingToXMLDecipher,
 *   convertToWordPreview,
 *   isConvertingToWordPreview,
 *   downloadDatamapCSV,
 *   isDownloadingDatamapCSV,
 *   initialiseData,
 *   isInitialisingData,
 *   RunEngineQuery,
 *   isRunningEngineQuery,
 *   exportToHTML,
 *   isExportingHTML,
 * } = useEngine();
 */
export const useEngine = (): UseEngineReturn => {
  const [convertToXMLDecipher, isConvertingToXMLDecipher] =
    useApiCommand(convertToDecipherXML)
  const [convertToXMLConfirmit, isConvertingToXMLConfirmit] =
    useApiCommand(convertToConfirmitXML)
  const [convertToWordPreview, isConvertingToWordPreview] =
    useApiCommand(convertToWord)
  const [downloadDatamapCSV, isDownloadingDatamapCSV] =
    useApiCommand(downloadDatamap)
  const [getDatamapCSV, isFetchingDatamapCSV] = useApiCommand(getDatamapCSVApi)
  const [initialiseData, isInitialisingData] = useApiCommand(
    InitialiseDataReporting
  )
  const [RunEngineQuery, isRunningEngineQuery] = useApiCommand(RunQuery)
  const [exportToHTML, isExportingHTML] = useApiCommand(downloadHTML)
  const [exportJSON, isExportingJSON] = useApiCommand(exportToJSON)
  const [exportJSONL, isExportingJSONL] = useApiCommand(exportToJSONL)
  const [exportSQL, isExportingSQL] = useApiCommand(exportToSQL)
  const [exportSPS, isExportingSPS] = useApiCommand(exportToSPS)
  const [exportSAV, isExportingSAV] = useApiCommand(exportToSAV)
  const [exportDQD, isExportingDQD] = useApiCommand(exportToDQD)
  const [exportCSV, isExportingCSV] = useApiCommand(exportToCSV)

  return {
    /**
     * Converts a project to Decipher XML format.
     */
    convertToXMLDecipher,

    /**
     * Indicates whether the conversion to Decipher XML is in progress.
     */
    isConvertingToXMLDecipher,

    /**
     * Converts a project to Confirmit (Forsta Plus) XML format.
     */
    convertToXMLConfirmit,

    /**
     * Indicates whether the conversion to Confirmit XML is in progress.
     */
    isConvertingToXMLConfirmit,

    /**
     * Converts a project to a Word preview format.
     */
    convertToWordPreview,

    /**
     * Indicates whether the conversion to Word preview is in progress.
     */
    isConvertingToWordPreview,

    /**
     * Downloads the Datamap as a CSV file.
     */
    downloadDatamapCSV,

    /**
     * Indicates whether the Datamap CSV download is in progress.
     */
    isDownloadingDatamapCSV,

    /**
     * Fetches the Datamap as a CSV string (no download) for in-app display.
     */
    getDatamapCSV,

    /**
     * Indicates whether the Datamap CSV fetch is in progress.
     */
    isFetchingDatamapCSV,

    /**
     * Initializes data reporting for a project.
     */
    initialiseData,

    /**
     * Indicates whether data reporting initialization is in progress.
     */
    isInitialisingData,

    /**
     * Runs an engine query on a project.
     */
    RunEngineQuery,

    /**
     * Indicates whether the engine query is in progress.
     */
    isRunningEngineQuery,

    /**
     * Exports the project to an HTML file.
     */
    exportToHTML,

    /**
     * Indicates whether the HTML export is in progress.
     */
    isExportingHTML,

    /**
     * Exports the project to a JSON file.
     */
    exportJSON,

    /**
     * Indicates whether the JSON export is in progress.
     */
    isExportingJSON,

    /**
     * Exports the project to a JSONL (JSON Lines) zip file.
     */
    exportJSONL,

    /**
     * Indicates whether the JSONL export is in progress.
     */
    isExportingJSONL,

    /**
     * Exports the project to an SQL file.
     */
    exportSQL,

    /**
     * Indicates whether the SQL export is in progress.
     */
    isExportingSQL,

    /**
     * Exports the project to an SPS file.
     */
    exportSPS,

    /**
     * Indicates whether the SPS export is in progress.
     */
    isExportingSPS,

    /**
     * Exports the project to a SAV (SPSS binary) file.
     */
    exportSAV,

    /**
     * Indicates whether the SAV export is in progress.
     */
    isExportingSAV,

    /**
     * Exports the project to a DQD (DataQ Binary) file.
     */
    exportDQD,

    /**
     * Indicates whether the DQD export is in progress.
     */
    isExportingDQD,

    /**
     * Exports the project to a CSV file.
     */
    exportCSV,

    /**
     * Indicates whether the CSV export is in progress.
     */
    isExportingCSV,
  }
}
